<template>
  <div class="relative pl-6 border-l-2 border-gray-100 dark:border-gray-800 space-y-8">
    <div v-for="(entry, idx) in props.history" :key="idx" class="relative">
      <!-- Dot -->
      <div
        :class="
          getHistoryColor(entry) === 'success'
            ? 'bg-green-500'
            : getHistoryColor(entry) === 'error'
              ? 'bg-red-500'
              : getHistoryColor(entry) === 'warning'
                ? 'bg-amber-500'
                : getHistoryColor(entry) === 'neutral'
                  ? 'bg-gray-400 dark:bg-gray-500'
                  : getHistoryColor(entry) === 'pink'
                    ? 'bg-pink-500'
                    : getHistoryColor(entry) === 'purple'
                      ? 'bg-purple-500'
                      : getHistoryColor(entry) === 'blue' || getHistoryColor(entry) === 'secondary'
                        ? 'bg-blue-500'
                        : 'bg-gray-400 dark:bg-gray-500'
        "
        class="absolute -left-[33px] mt-1.5 w-4 h-4 rounded-full border-4 border-white dark:border-gray-900 shadow-sm"
      ></div>

      <div
        :class="
          entry.branch === 'inquiry'
            ? (entry.action === 'Rückfrage beantwortet'
                ? 'flex flex-col space-y-2 min-w-0 rounded-lg border border-purple-100/80 dark:border-purple-800/40 bg-purple-50/40 dark:bg-purple-900/10 p-3'
                : 'flex flex-col space-y-2 min-w-0 rounded-lg border border-pink-100/80 dark:border-pink-800/40 bg-pink-50/40 dark:bg-pink-900/10 p-3')
            : 'flex flex-col space-y-2 min-w-0'
        "
      >
        <div class="flex justify-between items-start gap-2 min-w-0">
          <div class="flex items-center gap-2 min-w-0">
            <span
              :class="[
                'text-sm font-bold leading-tight truncate',
                entry.branch === 'inquiry'
                  ? (entry.action === 'Rückfrage beantwortet' ? 'text-purple-700 dark:text-purple-300' : 'text-pink-700 dark:text-pink-300')
                  : 'text-gray-900 dark:text-white'
              ]"
            >
              {{ entry.branch === 'inquiry' ? (entry.action === 'Rückfrage beantwortet' ? 'ANTWORT' : 'RÜCKFRAGE') : entry.stepName }}
            </span>
          </div>
          <UBadge
            :color="getHistoryColor(entry)"
            class="capitalize shrink-0"
            size="sm"
            variant="soft"
          >
            <template #leading>
              <UIcon v-if="entry.icon" :name="entry.icon" class="w-3.5 h-3.5 mr-1" />
            </template>
            {{ translateAction(entry.action) }}
          </UBadge>
        </div>

        <div class="flex flex-col gap-1">
          <div class="flex items-center justify-between gap-3 min-w-0">
            <div class="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 min-w-0 flex-1">
              <UIcon class="w-3.5 h-3.5 shrink-0" name="i-heroicons-user" />
              <span :title="entry.actor" class="font-medium truncate">
                {{ entry.actor === 'SYSTEM' ? 'System' : entry.actor }}
              </span>
            </div>
            <div class="flex items-center gap-1.5 text-[10px] text-gray-400 shrink-0">
              <UIcon class="w-3.5 h-3.5" name="i-heroicons-calendar" />
              <span>{{ formatDate(entry.timestamp) }}</span>
            </div>
          </div>
          <div
            v-if="entry.group || entry.user"
            class="flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500 italic"
          >
            <div v-if="entry.group" class="flex items-center gap-1.5 min-w-0">
              <UIcon class="w-3.5 h-3.5 shrink-0" name="i-heroicons-user-group" />
              <span class="truncate">{{ resolveGroupName(entry.group) }}</span>
            </div>
            <div v-if="entry.user" class="flex items-center gap-1.5 min-w-0">
              <UIcon class="w-3.5 h-3.5 shrink-0" name="i-heroicons-user" />
              <span class="truncate">{{ entry.user }}</span>
            </div>
          </div>
        </div>

        <p
          v-if="entry.comment"
          :class="[
            'text-xs italic p-3 rounded-lg border relative whitespace-pre-wrap transition-colors duration-200',
            entry.branch === 'inquiry'
              ? (entry.action === 'Rückfrage beantwortet'
                  ? 'text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 border-purple-100 dark:border-purple-800/50'
                  : 'text-pink-700 dark:text-pink-300 bg-pink-50 dark:bg-pink-900/30 border-pink-100 dark:border-pink-800/50')
              : (getHistoryColor(entry) === 'warning'
                  ? 'text-amber-800 dark:text-amber-200 bg-amber-50/70 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800/50'
                  : 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/80 border-gray-100 dark:border-gray-700/50')
          ]"
        >
          <UIcon
            :class="[
              'absolute -top-2 -right-2 w-4 h-4',
              entry.branch === 'inquiry'
                ? (entry.action === 'Rückfrage beantwortet' ? 'text-purple-300 dark:text-purple-600' : 'text-pink-300 dark:text-pink-600')
                : (getHistoryColor(entry) === 'warning' ? 'text-amber-400 dark:text-amber-500' : 'text-gray-300 dark:text-gray-600')
            ]"
            :name="getHistoryColor(entry) === 'warning' ? 'i-heroicons-exclamation-triangle' : 'i-heroicons-chat-bubble-left-right'"
          />
          "{{ entry.comment }}"
        </p>
      </div>
    </div>
    <div
      v-if="props.history.length === 0"
      class="text-sm text-gray-400 dark:text-gray-500 italic text-center py-8"
    >
      Noch keine Schritte durchgeführt.
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { HistoryEntry } from '@/types'
import { translateAction, getActionColor } from '@/utils/translations'
import { useGroupNames } from '@/composables/useGroupNames'

const { resolveGroupName } = useGroupNames()

const props = defineProps<{
  history: HistoryEntry[]
}>()

const getHistoryColor = (entry: HistoryEntry) => {
  return getActionColor(entry.action, entry.color)
}

const formatDate = (ts: string) => {
  if (!ts) return ''
  return new Date(ts).toLocaleString('de-DE')
}
</script>
